import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";

const ACTIVICA_PIXEL =
    '"FT Activica Pixel", "FT Activica Normal", monospace';
const FIGTREE = '"Figtree", "Inter", system-ui, sans-serif';

const ACCENTS = ["#D2FE55", "#05C4E4", "#DE7237"];

export default function MobileCities() {
    const [cards, setCards] = useState([]);
    const [centerIdx, setCenterIdx] = useState(0);
    const scrollerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/city-cards")
            .then((r) => {
                if (cancelled || !Array.isArray(r.data)) return;
                const sorted = [...r.data].sort(
                    (a, b) => (a.slot_index ?? 0) - (b.slot_index ?? 0),
                );
                setCards(sorted);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    // Track which card is at viewport center → drives the CTA city name.
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || cards.length === 0) return;
        const onScroll = () => {
            const center = el.scrollLeft + el.clientWidth / 2;
            const items = el.querySelectorAll("[data-card-idx]");
            let bestIdx = 0;
            let bestDist = Infinity;
            items.forEach((node) => {
                const c = node.offsetLeft + node.offsetWidth / 2;
                const d = Math.abs(c - center);
                if (d < bestDist) {
                    bestDist = d;
                    bestIdx = Number(node.getAttribute("data-card-idx"));
                }
            });
            setCenterIdx(bestIdx);
        };
        onScroll();
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, [cards]);

    const activeCity = cards[centerIdx]?.city_name || "";

    return (
        <section
            id="community"
            data-testid="cities-section-mobile"
            className="relative bg-background pt-20 pb-10 overflow-hidden"
        >
            <h2
                data-testid="cities-heading-mobile"
                className="text-center px-6 mb-10"
                style={{
                    fontFamily: ACTIVICA_PIXEL,
                    fontWeight: 400,
                    fontSize: "clamp(2.2rem, 9vw, 3.2rem)",
                    lineHeight: 1.05,
                    letterSpacing: "0.04em",
                    color: "#FFFCE1",
                    textTransform: "uppercase",
                }}
            >
                <span style={{ color: "#FFFCE1" }}>City </span>
                <span
                    style={{
                        background:
                            "linear-gradient(180deg, #90FDA6 -0.02%, #F8FF28 132.41%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        color: "transparent",
                    }}
                >
                    Chapters
                </span>
            </h2>

            {/* Swipeable card row — taller portrait cards */}
            <div
                ref={scrollerRef}
                data-testid="cities-scroller-mobile"
                className="vc-cities-scroller"
            >
                <div className="shrink-0" style={{ width: "16vw" }} />
                {cards.map((c, i) => (
                    <CityCard key={c.id} card={c} idx={i} />
                ))}
                <div className="shrink-0" style={{ width: "16vw" }} />
            </div>

            {/* Persistent Apply Now CTA — same width as a card, always
                on screen, pinned below the carousel. */}
            <div
                className="mt-6 flex justify-center"
                data-testid="cities-cta-mobile-wrap"
            >
                <Link
                    to={`/apply?city=${encodeURIComponent(activeCity)}`}
                    data-testid="cities-cta-mobile"
                    className="block text-center hover:brightness-105 transition-[filter]"
                    style={{
                        width: "68vw",
                        maxWidth: 320,
                        padding: "14px 20px",
                        background:
                            "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                        boxShadow:
                            "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                        borderRadius: 12,
                        fontFamily: FIGTREE,
                        fontWeight: 700,
                        fontSize: 14,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#0E100F",
                        textDecoration: "none",
                    }}
                >
                    {activeCity
                        ? `Lead ${activeCity}`
                        : "Lead a Chapter"}
                </Link>
            </div>
        </section>
    );
}

function CityCard({ card, idx }) {
    const img = card.image_url
        ? card.image_url.startsWith("/")
            ? `${window.location.origin}${card.image_url}`
            : card.image_url
        : null;
    const accent = ACCENTS[(card.slot_index ?? idx) % ACCENTS.length];

    return (
        <div
            data-testid={`city-card-mobile-${card.slot_index ?? idx}`}
            data-card-idx={idx}
            className="shrink-0 relative overflow-hidden"
            style={{
                width: "68vw",
                maxWidth: 320,
                aspectRatio: "3 / 4",
                scrollSnapAlign: "center",
                scrollSnapStop: "always",
                background: accent,
                borderRadius: 24,
                padding: "16px 16px 0",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <div
                className="relative"
                style={{
                    flex: 1,
                    background: "#E4E1F2",
                    borderRadius: 18,
                    overflow: "hidden",
                }}
            >
                {img && (
                    <img
                        src={img}
                        alt={card.city_name}
                        className="w-full h-full"
                        loading="lazy"
                        style={{ objectFit: "cover" }}
                    />
                )}
            </div>
            <div
                style={{
                    fontFamily: ACTIVICA_PIXEL,
                    fontWeight: 400,
                    fontSize: "clamp(1.4rem, 5.8vw, 2rem)",
                    lineHeight: 1.0,
                    letterSpacing: "0.04em",
                    textAlign: "center",
                    textTransform: "uppercase",
                    color: "#0E100F",
                    padding: "16px 8px 20px",
                }}
            >
                {card.city_name}
            </div>
        </div>
    );
}
